import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { addMonths, endOfMonth, startOfMonth } from 'date-fns';
import type { CalendarEvent, CalendarInterval, DisplayEvent } from '../types';
import { EventType } from '../types';
import { GlobalMockupService } from '@/common/services/mockup';
import { MockAgendaService } from '../services';
import { agendaService } from '../services';
import { createDisplayEvents } from '../utils/DisplayMethods';

import { logger } from '@/common/services/config';
export const useAgendaStore = defineStore('agenda', () => {
  // State
  const events = ref<CalendarEvent[]>([]);
  const displayEvents = ref<DisplayEvent[]>([]); // Display events for calendar rendering
  const selectedEvent = ref<CalendarEvent | null>(null);
  const personalEvent = ref<any>({});
  const isLoading = ref(false);
  const isUpdating = ref(false);
  const error = ref<string | null>(null);

  // Date interval for calendar data
  const todayDate = new Date();
  const startIntervalDate = ref(startOfMonth(addMonths(todayDate, -4)).toISOString());
  const endIntervalDate = ref(endOfMonth(addMonths(todayDate, 6)).toISOString());
  const currentDate = ref(todayDate.toISOString());

  // Getters
  const currentInterval = computed<CalendarInterval>(() => ({
    start: startIntervalDate.value,
    end: endIntervalDate.value,
  }));

  const myEvents = computed(() =>
    displayEvents.value.filter(
      (event) =>
        event.originalEvent.type === EventType.PersonalEvent &&
        'isMine' in event.originalEvent &&
        event.originalEvent.isMine
    )
  );

  const eventsForDate = computed(() => (date: string) => {
    const targetDate = new Date(date).toDateString();
    return displayEvents.value.filter((event) => {
      const eventDate = new Date(event.start).toDateString();
      return eventDate === targetDate;
    });
  });

  // Helper to determine if we should show the main loading skeleton
  const shouldShowMainLoading = computed(() => {
    return isLoading.value && events.value.length === 0;
  });

  // Helper function to convert mock events to CalendarEvent format
  function convertMockEventsToCalendarEvents(mockEvents: any[]): CalendarEvent[] {
    return mockEvents.map((event) => {
      // Map event types directly since they should match
      let calendarEventType: CalendarEvent['type'];
      switch (event.type) {
        case 'Activity':
          calendarEventType = EventType.Activity;
          break;
        case 'Appointment':
          calendarEventType = EventType.PersonalEvent;
          break;
        case 'Seance':
          calendarEventType = EventType.Session;
          break;
        case 'Examen':
        case 'Exam':
          calendarEventType = EventType.Exam;
          break;
        default:
          calendarEventType = EventType.Activity;
      }
      return {
        id: event.id.toString(),
        type: calendarEventType,
        label: event.label || event.title || 'No Title',
        start: event.start,
        end: event.end,
        description: event.description || undefined,
        locations: event.locations,
        ...(calendarEventType === EventType.PersonalEvent && {
          isMine: event.isMine || false,
          rsvp: event.rsvp,
          teamsLink: event.teams_link || event.teamsLink,
          attendees: event.attendees,
        }),
        ...(calendarEventType === EventType.Activity && {
          teamsLink: event.teams_link || event.teamsLink,
          moodleLink: event.moodle_link || event.moodleLink,
          interventionPlace: event.interventionPlace,
          interventionPlaceAddress: event.interventionPlaceAddress,
          moreInfo: event.more_info || event.moreInfo,
          teacher: event.teacher,
        }),
      } as CalendarEvent;
    });
  }

  async function fetchEvents(interval?: CalendarInterval): Promise<CalendarEvent[]> {
    const hasNoData = events.value.length === 0;

    if (hasNoData) {
      setLoading(true);
    } else {
      const queryInterval = interval || currentInterval.value;
      if (isIntervalCovered(queryInterval)) {
        return events.value;
      }
      setUpdating(true);
    }
    setError(null);

    try {
      let fetchedEvents: CalendarEvent[] = [];

      if (GlobalMockupService.isFeatureMockupEnabled('agenda')) {
        const queryInterval = interval || currentInterval.value;
        const mockEvents = await MockAgendaService.getEvents(
          queryInterval.start,
          queryInterval.end
        );
        fetchedEvents = convertMockEventsToCalendarEvents(mockEvents);
      } else {
        const queryInterval = interval || currentInterval.value;
        const response = await agendaService.fetchEvents(queryInterval);
        fetchedEvents = response;
      }

      // Merge new events with existing ones instead of replacing
      const mergedEvents = hasNoData ? fetchedEvents : mergeEvents(events.value, fetchedEvents);
      setEvents(mergedEvents);
      return mergedEvents;
    } catch (err) {
      logger.error('Failed to fetch events in store:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch events');
      throw err;
    } finally {
      if (hasNoData) {
        setLoading(false);
      } else {
        setUpdating(false);
      }
    }
  }

  // Helper function to check if interval is already covered by cached data
  function isIntervalCovered(requestedInterval: CalendarInterval): boolean {
    const currentStart = new Date(startIntervalDate.value);
    const currentEnd = new Date(endIntervalDate.value);
    const requestedStart = new Date(requestedInterval.start);
    const requestedEnd = new Date(requestedInterval.end);

    // Check if the requested interval is completely within the cached interval
    return requestedStart >= currentStart && requestedEnd <= currentEnd;
  }

  // Helper function to merge new events with existing ones, removing duplicates
  function mergeEvents(
    existingEvents: CalendarEvent[],
    newEvents: CalendarEvent[]
  ): CalendarEvent[] {
    const eventMap = new Map<string, CalendarEvent>();

    // Add existing events to map
    existingEvents.forEach((event) => {
      eventMap.set(event.id, event);
    });

    // Add/update with new events (overwrites duplicates)
    newEvents.forEach((event) => {
      eventMap.set(event.id, event);
    });

    return Array.from(eventMap.values());
  }

  async function updateEventsForInterval(interval: CalendarInterval): Promise<CalendarEvent[]> {
    const needsFetch = !isIntervalCovered(interval);
    const hasNoData = events.value.length === 0;

    if (!needsFetch && !hasNoData) {
      return events.value; // Return existing events without API call
    }

    // Use isLoading for initial load when no data, isUpdating for range updates
    if (hasNoData) {
      setLoading(true);
    } else {
      setUpdating(true);
    }
    setError(null);

    try {
      let fetchedEvents: CalendarEvent[] = [];

      // Check if mockup mode is enabled for agenda
      if (GlobalMockupService.isFeatureMockupEnabled('agenda')) {
        const mockEvents = await MockAgendaService.getEvents(interval.start, interval.end);
        fetchedEvents = convertMockEventsToCalendarEvents(mockEvents);
      } else {
        const response = await agendaService.fetchEvents(interval);
        fetchedEvents = response;
      }

      // Update the cached interval to cover the new range
      const currentStart = new Date(startIntervalDate.value);
      const currentEnd = new Date(endIntervalDate.value);
      const requestedStart = new Date(interval.start);
      const requestedEnd = new Date(interval.end);

      const newStart = requestedStart < currentStart ? requestedStart : currentStart;
      const newEnd = requestedEnd > currentEnd ? requestedEnd : currentEnd;

      updateInterval({
        start: newStart.toISOString(),
        end: newEnd.toISOString(),
      });

      const mergedEvents = hasNoData ? fetchedEvents : mergeEvents(events.value, fetchedEvents);
      setEvents(mergedEvents);
      return mergedEvents;
    } catch (err) {
      logger.error('Failed to update events in store:', err);
      setError(err instanceof Error ? err.message : 'Failed to update events');
      throw err;
    } finally {
      // Reset the appropriate loading state
      if (hasNoData) {
        setLoading(false);
      } else {
        setUpdating(false);
      }
    }
  }

  async function createEventInStore(eventData: any): Promise<CalendarEvent> {
    setLoading(true);
    setError(null);

    try {
      let newEvent: CalendarEvent;

      if (GlobalMockupService.isFeatureMockupEnabled('agenda')) {
        const mockEvent = await MockAgendaService.createEvent(eventData);
        newEvent = convertMockEventsToCalendarEvents([mockEvent])[0];
      } else {
        const response = await agendaService.createAppointment(eventData);
        newEvent = response;
      }

      addEvent(newEvent);
      return newEvent;
    } catch (err) {
      logger.error('Failed to create event in store:', err);
      setError(err instanceof Error ? err.message : 'Failed to create event');
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function updateEventInStore(id: string, eventData: any): Promise<CalendarEvent> {
    setLoading(true);
    setError(null);

    try {
      let updatedEvent: CalendarEvent;

      if (GlobalMockupService.isFeatureMockupEnabled('agenda')) {
        const mockEvent = await MockAgendaService.updateEvent(id, eventData);
        updatedEvent = convertMockEventsToCalendarEvents([mockEvent])[0];
      } else {
        const response = await agendaService.updateAppointment(id, eventData);
        updatedEvent = response;
      }

      updateEvent(updatedEvent);
      return updatedEvent;
    } catch (err) {
      logger.error('Failed to update event in store:', err);
      setError(err instanceof Error ? err.message : 'Failed to update event');
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function deleteEventInStore(id: string): Promise<void> {
    setLoading(true);
    setError(null);

    try {
      if (GlobalMockupService.isFeatureMockupEnabled('agenda')) {
        await MockAgendaService.deleteEvent(id);
      } else {
        await agendaService.deleteAppointment(id);
      }

      removeEvent(id);
    } catch (err) {
      logger.error('Failed to delete event in store:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete event');
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function acceptEventRsvpInStore(id: string): Promise<void> {
    setLoading(true);
    setError(null);

    try {
      if (GlobalMockupService.isFeatureMockupEnabled('agenda')) {
        await MockAgendaService.acceptAppointmentRsvp(id);
      } else {
        await agendaService.acceptAppointmentRsvp(id);
      }

      // Optionally update the event's RSVP status in the store
      const event = events.value.find((event) => event.id === id);
      if (event && event.type === EventType.PersonalEvent) {
        event.rsvp = true;
        updateEvent(event);
      }
    } catch (err) {
      logger.error('Failed to accept RSVP in store:', err);
      setError(err instanceof Error ? err.message : 'Failed to accept RSVP');
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function declineEventRsvpInStore(id: string, comments: string = ''): Promise<void> {
    setLoading(true);
    setError(null);

    try {
      if (GlobalMockupService.isFeatureMockupEnabled('agenda')) {
        await MockAgendaService.declineAppointmentRsvp(id);
      } else {
        await agendaService.declineAppointmentRsvp(id, comments);
      }

      // Optionally update the event's RSVP status in the store
      const event = events.value.find((event) => event.id === id);
      if (event && event.type === EventType.PersonalEvent) {
        event.rsvp = false;
        updateEvent(event);
      }
    } catch (err) {
      logger.error('Failed to decline RSVP in store:', err);
      setError(err instanceof Error ? err.message : 'Failed to decline RSVP');
      throw err;
    } finally {
      setLoading(false);
    }
  }

  // Helper functions for error and loading state
  function setError(errorMessage: string | null) {
    error.value = errorMessage;
  }

  function setEvents(newEvents: CalendarEvent[]) {
    events.value = newEvents;
    setDisplayEvents(newEvents);
  }

  function setDisplayEvents(originalEvents: CalendarEvent[]) {
    displayEvents.value = createDisplayEvents(originalEvents);
  }

  function addEvent(event: CalendarEvent) {
    events.value.push(event);
    setDisplayEvents(events.value);
  }

  function updateEvent(updatedEvent: CalendarEvent) {
    const index = events.value.findIndex((event) => event.id === updatedEvent.id);
    if (index !== -1) {
      events.value[index] = updatedEvent;
      setDisplayEvents(events.value);
    }
    if (selectedEvent.value?.id === updatedEvent.id) {
      selectedEvent.value = updatedEvent;
    }
  }

  function removeEvent(eventId: string) {
    events.value = events.value.filter((event) => event.id !== eventId);
    setDisplayEvents(events.value);
    if (selectedEvent.value?.id === eventId) {
      selectedEvent.value = null;
    }
  }

  function setSelectedEvent(event: CalendarEvent | null) {
    selectedEvent.value = event;
  }

  function setLoading(loading: boolean) {
    isLoading.value = loading;
  }

  function setUpdating(updating: boolean) {
    isUpdating.value = updating;
  }

  function updateInterval(interval: CalendarInterval) {
    startIntervalDate.value = interval.start;
    endIntervalDate.value = interval.end;
  }

  function resetStore() {
    events.value = [];
    displayEvents.value = [];
    selectedEvent.value = null;
    isLoading.value = false;
    isUpdating.value = false;
    error.value = null;
    // Reset interval dates to initial values
    const todayDate = new Date();
    startIntervalDate.value = startOfMonth(addMonths(todayDate, -4)).toISOString();
    endIntervalDate.value = endOfMonth(addMonths(todayDate, 6)).toISOString();
  }

  return {
    // State
    events,
    displayEvents,
    selectedEvent,
    personalEvent,
    isLoading,
    isUpdating,
    error,
    startIntervalDate,
    endIntervalDate,
    currentDate,

    // Getters
    currentInterval,
    myEvents,
    eventsForDate,
    shouldShowMainLoading,

    // Data fetching actions (similar to agendaSlice async actions)
    fetchEvents,
    updateEventsForInterval,
    createEventInStore,
    updateEventInStore,
    deleteEventInStore,
    acceptEventRsvpInStore,
    declineEventRsvpInStore,

    // Basic Actions (similar to agendaSlice reducers)
    setEvents,
    setDisplayEvents,
    addEvent,
    updateEvent,
    removeEvent,
    setSelectedEvent,
    setLoading,
    setUpdating,
    setError,
    updateInterval,
    resetStore,
  };
});
